import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { DESCRICAO_PADRAO_DO_PAPEL, GRUPOS_DE_PERMISSOES } from '../dados/permissoes';
import { Carregando, ErroBox, Modal, ModalRodape } from './ui';

export interface Papel {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  global: boolean;
  permissions: string[];
}

/**
 * O que cada papel pode fazer, lado a lado.
 *
 * Comparação e não uma ficha por papel: a pergunta real de quem abre esta tela
 * nunca é "o que o Operador faz", é "qual deles eu dou para esta pessoa" — e
 * essa só se responde vendo a diferença entre eles.
 *
 * Os papéis vêm do servidor, não de uma cópia aqui: papel é dado, a organização
 * pode ter os seus, e uma lista fixa na tela mostraria permissão que a pessoa
 * não tem — ou esconderia a que ela tem.
 */
export function PapeisEPermissoes({ aoFechar }: { aoFechar: () => void }) {
  const papeis = useQuery({ queryKey: ['papeis'], queryFn: () => api<Papel[]>('/roles') });

  const lista = papeis.data ?? [];
  const concedidas = new Map(lista.map((p) => [p.id, new Set(p.permissions)]));

  return (
    <Modal
      titulo="Papéis e permissões"
      descricao="O papel decide o que a pessoa enxerga e pode fazer. O acesso a eventos específicos é separado, definido por pessoa."
      aoFechar={aoFechar}
      largura={860}
    >
      {papeis.isPending && <Carregando />}
      {papeis.isError && <ErroBox erro={papeis.error} aoTentar={() => papeis.refetch()} />}

      {papeis.data && (
        <>
          <div className="lista-papeis">
            {lista.map((papel) => (
              <div key={papel.id} className="papel-resumo">
                <div className="papel-nome">
                  {papel.name}
                  {!papel.global && <span className="papel-marca">próprio</span>}
                </div>
                <div className="papel-descricao">
                  {papel.description ||
                    DESCRICAO_PADRAO_DO_PAPEL[papel.slug] ||
                    'Papel criado por esta organização — veja na tabela o que ele alcança.'}
                </div>
                <div className="papel-contagem">
                  {papel.permissions.length} de {totalDeSlugs()} permissões
                </div>
              </div>
            ))}
          </div>

          <div className="tabela-envolvente">
            <table className="data-table tabela-permissoes">
              <thead>
                <tr>
                  <th>Permissão</th>
                  {lista.map((papel) => (
                    <th
                      key={papel.id}
                      className="coluna-papel"
                      title={papel.description ?? DESCRICAO_PADRAO_DO_PAPEL[papel.slug] ?? ''}
                    >
                      {papel.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {GRUPOS_DE_PERMISSOES.map((grupo) => {
                  // Só as que o servidor realmente conhece: se um slug sumir do
                  // catálogo, some da tabela em vez de virar uma linha em que
                  // nenhum papel marca nada — o que se leria como "ninguém
                  // pode", e não como "isto não existe mais".
                  const doGrupo = grupo.permissoes.filter((p) =>
                    lista.some((papel) => concedidas.get(papel.id)?.has(p.slug)),
                  );
                  if (doGrupo.length === 0) return null;

                  return (
                    <Fragment key={grupo.titulo}>
                      <tr className="linha-grupo">
                        <td colSpan={lista.length + 1}>
                          <i className={`ti ${grupo.icone}`} /> {grupo.titulo}
                        </td>
                      </tr>

                      {doGrupo.map((permissao) => (
                        <tr key={permissao.slug}>
                          <td>
                            <div className="celula-principal">{permissao.rotulo}</div>
                            {permissao.detalhe && (
                              <div className="celula-secundaria">{permissao.detalhe}</div>
                            )}
                          </td>
                          {lista.map((papel) => {
                            const tem = concedidas.get(papel.id)?.has(permissao.slug);
                            return (
                              <td key={papel.id} className="coluna-papel">
                                {tem ? (
                                  <i
                                    className="ti ti-check"
                                    style={{ color: 'var(--success)' }}
                                    aria-label="permitido"
                                  />
                                ) : (
                                  <i
                                    className="ti ti-minus"
                                    style={{ color: 'var(--text-dim)' }}
                                    aria-label="não permitido"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="aviso" style={{ marginTop: 16 }}>
            <i className="ti ti-info-circle" />
            <span>
              Uma pessoa sem papel entra no painel e não vê nada. Dar{' '}
              <strong>Gerenciar usuários e papéis</strong> é dar, junto, o poder de conceder
              qualquer permissão desta lista a qualquer pessoa.
            </span>
          </div>
        </>
      )}

      <ModalRodape>
        <button className="btn btn-primary" onClick={aoFechar}>
          Fechar
        </button>
      </ModalRodape>
    </Modal>
  );
}

function totalDeSlugs() {
  return GRUPOS_DE_PERMISSOES.reduce((soma, grupo) => soma + grupo.permissoes.length, 0);
}
